import React, { useState } from "react";
import { toast } from "sonner";
import { Crosshair, MapPin, X, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { API_BASE } from "@/lib/api";
import { useReferences } from "@/hooks/useReferences";
import { MultiSelect } from "./MultiSelect";
import { isRegionCenter } from "./GeoRadiusFilter";

// Per-directory filter groups. `key` = backend query param; `ref` = reference list key.
export const DIR_FILTERS = {
  people: [
    { key: "classifications", label: "Classification", ref: "profile_classifications" },
    { key: "industries", label: "Industry", ref: "industries" },
    { key: "skills", label: "Skills & interests", ref: "skills_interests" },
  ],
  businesses: [
    { key: "categories", label: "Category", ref: "profile_categories" },
    { key: "stages", label: "Stage", ref: "funding_stages" },
    { key: "company_sizes", label: "Company size", ref: "company_sizes" },
    { key: "industries", label: "Industry", ref: "industries" },
    { key: "classifications", label: "Classification", ref: "business_classifications" },
  ],
  capital: [
    { key: "capital_types", label: "Capital type", ref: "capital_types" },
    { key: "check_sizes", label: "Check size", ref: "check_sizes" },
    { key: "stages", label: "Stage", ref: "funding_stages" },
    { key: "industries", label: "Industry", ref: "industries" },
  ],
  // Groups reuse the SAME shared filter groups as the other directories — including the
  // canonical Business `business_classifications` reference data (GRP-DEC-286) — plus the
  // Group-specific Category and Topics lists. Group-only concepts (membership type,
  // online) are passed in through `extra`.
  groups: [
    { key: "categories", label: "Category", ref: "group_categories" },
    { key: "classifications", label: "Classification", ref: "business_classifications" },
    { key: "industries", label: "Industry", ref: "industries" },
    { key: "topics", label: "Topics & interests", ref: "skills_interests" },
  ],
};

export const DirectoryFilters = ({ kind, value, onChange, onClear, hasActive, isAdmin = false, extra = null }) => {
  const { references } = useReferences();
  // People → "Classification" filter is gated to admins only (hidden for the public).
  // Backend/functionality untouched; this only controls visibility of the control.
  const groups = (DIR_FILTERS[kind] || []).filter(
    (g) => !(kind === "people" && g.key === "classifications" && !isAdmin)
  );
  const [locInput, setLocInput] = useState("");
  const [locBusy, setLocBusy] = useState(false);

  const setMulti = (key, arr) => onChange({ multi: { ...value.multi, [key]: arr } });

  const lookup = async () => {
    if (!locInput.trim()) return;
    setLocBusy(true);
    try {
      const r = await fetch(`${API_BASE}/directory/geo?q=${encodeURIComponent(locInput.trim())}`, { credentials: "include" });
      if (!r.ok) throw new Error("not found");
      const data = await r.json();
      onChange({ geoOn: true, center: {
        lat: data.lat, lng: data.lng, label: data.formatted || locInput.trim(),
        granularity: data.granularity, state_code: data.state_code, country_code: data.country_code,
      } });
    } catch (e) {
      toast.error("Couldn't find that location. Try a city, state, province, or ZIP.");
    } finally {
      setLocBusy(false);
    }
  };

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation isn't supported on this device."); return; }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ geoOn: true, center: { lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" } });
        setLocBusy(false);
      },
      () => { toast.error("Couldn't access your location."); setLocBusy(false); },
      { timeout: 8000 }
    );
  };

  return (
    <Card className="border-0 p-5" data-testid="directory-filters-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Filters</h3>
        {hasActive && (
          <button onClick={onClear} className="text-xs font-[540] text-primary hover:underline" data-testid="directory-clear-filters">Clear all</button>
        )}
      </div>

      <div className="mt-4 space-y-4">
        {groups.map((g) => (
          <div key={g.key}>
            <Label className="mb-1.5 block text-xs font-[600] text-muted-foreground">{g.label}</Label>
            <MultiSelect
              label={`Any ${g.label.toLowerCase()}`}
              options={references[g.ref] || []}
              selected={value.multi[g.key] || []}
              onChange={(arr) => setMulti(g.key, arr)}
              testId={`filter-${g.key}`}
            />
          </div>
        ))}
      </div>

      {/* Entity-specific filters (e.g. a Group's membership type) render with the same
          spacing as the shared groups above. Nothing is rendered when absent, so People /
          Businesses / Capital are byte-identical. */}
      {extra ? <div className="mt-4 space-y-4">{extra}</div> : null}

      <Separator className="my-5" />

      <div className="flex items-center justify-between">
        <Label htmlFor="geo-switch" className="text-sm font-[560]">Search by distance</Label>
        <Switch id="geo-switch" checked={value.geoOn} onCheckedChange={(v) => onChange({ geoOn: v })} data-testid="directory-geo-toggle" />
      </div>

      {value.geoOn && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            <Input
              value={locInput}
              onChange={(e) => setLocInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
              placeholder="City, state, province, or ZIP"
              data-testid="directory-location-input"
            />
            <Button variant="secondary" size="icon" onClick={locateMe} disabled={locBusy} title="Use my location" data-testid="directory-locate-me">
              {locBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
            </Button>
          </div>

          {value.center && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="truncate">{value.center.label}</span>
              <button onClick={() => onChange({ center: null })} className="ml-auto text-muted-foreground hover:text-foreground" title="Clear location"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}

          {value.center && isRegionCenter(value.center) ? (
            <p className="text-xs text-muted-foreground" data-testid="directory-region-note">
              Showing everyone across <span className="font-[600] text-foreground">{value.center.label}</span>. The distance radius doesn&apos;t apply to a whole state or country.
            </p>
          ) : (
            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="font-[600] text-muted-foreground">Within</span>
                <span className="font-[640] text-foreground" data-testid="directory-radius-value">{value.radius} mi</span>
              </div>
              <Slider min={5} max={500} step={5} value={[value.radius]} onValueChange={(v) => onChange({ radius: v[0] })} data-testid="directory-radius-slider" />
            </div>
          )}

          {!value.center && (
            <p className="text-xs text-muted-foreground">Enter a location or use your current location to filter by distance.</p>
          )}
        </div>
      )}
    </Card>
  );
};
